/** Revision session configurator combining content selection, question filters, and launch. */
import { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import type { RevisableCourse, RevisablePersonalLesson } from '@formency/shared';
import { Button, Slider } from '@/interfaces/learner/ui';
import ReviseContentPicker from './ReviseContentPicker';
import QuestionTypeFilter from './QuestionTypeFilter';
import { ALL_QUESTION_TYPES } from './questionTypes';

const EASE = [0.16, 1, 0.3, 1] as const;

interface SessionCreatorProps {
  courses: RevisableCourse[];
  personalLessons: RevisablePersonalLesson[];
  onStart: (config: { lessonIds: string[]; questionTypes: string[]; maxQuestions: number }) => void;
  onCancel?: () => void;
  isCreating: boolean;
  error?: string | null;
}

export default function SessionCreator({
  courses,
  personalLessons,
  onStart,
  onCancel,
  isCreating,
  error,
}: SessionCreatorProps) {
  const [selectedLessonIds, setSelectedLessonIds] = useState<Set<string>>(new Set());
  const [selectedQuestionTypes, setSelectedQuestionTypes] = useState<Set<string>>(
    new Set(ALL_QUESTION_TYPES)
  );
  const [maxQuestions, setMaxQuestions] = useState(20);

  const { availableTypes, typeCounts, totalAvailable } = useMemo(() => {
    const types = new Set<string>();
    const counts = new Map<string, number>();
    let total = 0;

    function walkLessons(
      lessons: Array<{ id: string; questionTypes: string[]; reviseStepCount: number }>
    ) {
      for (const lesson of lessons) {
        if (!selectedLessonIds.has(lesson.id)) continue;
        for (const qt of lesson.questionTypes) {
          types.add(qt);
          if (selectedQuestionTypes.has(qt)) {
            counts.set(
              qt,
              (counts.get(qt) ?? 0) +
                Math.ceil(lesson.reviseStepCount / lesson.questionTypes.length)
            );
          }
        }
        const matching = lesson.questionTypes.filter((qt) => selectedQuestionTypes.has(qt));
        if (matching.length > 0) {
          total += Math.ceil(
            lesson.reviseStepCount * (matching.length / lesson.questionTypes.length)
          );
        }
      }
    }

    for (const course of courses) {
      for (const mod of course.modules) {
        for (const topic of mod.topics) {
          walkLessons(topic.lessons);
        }
      }
    }
    walkLessons(personalLessons);

    return { availableTypes: types, typeCounts: counts, totalAvailable: total };
  }, [courses, personalLessons, selectedLessonIds, selectedQuestionTypes]);

  function handleToggleType(type: string) {
    setSelectedQuestionTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type)) {
        if (next.size > 1) next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  }

  const effectiveMax = Math.min(maxQuestions, totalAvailable);
  const showTypes = selectedLessonIds.size > 0 && availableTypes.size > 0;
  const showSlider = totalAvailable > 0;

  return (
    <div>
      <p className="mb-4" style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
        Pick content and question types, then start a revision session.
      </p>

      <div className="flex flex-col gap-5">
        <ReviseContentPicker
          courses={courses}
          personalLessons={personalLessons}
          selectedLessonIds={selectedLessonIds}
          onSelectionChange={setSelectedLessonIds}
        />

        <AnimatePresence initial={false}>
          {showTypes && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.25, ease: EASE }}
              style={{ overflow: 'hidden' }}
            >
              <QuestionTypeFilter
                selectedTypes={selectedQuestionTypes}
                availableTypes={availableTypes}
                typeCounts={typeCounts}
                onToggle={handleToggleType}
              />
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence initial={false}>
          {showSlider && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.25, ease: EASE }}
              style={{ overflow: 'hidden' }}
            >
              <div className="flex flex-col gap-4">
                <Slider
                  label="Number of Questions"
                  minValue={1}
                  maxValue={totalAvailable}
                  step={1}
                  value={effectiveMax}
                  output={effectiveMax}
                  onChange={setMaxQuestions}
                />

                {/* Summary */}
                <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
                  ~{effectiveMax} questions from {selectedLessonIds.size}{' '}
                  {selectedLessonIds.size === 1 ? 'lesson' : 'lessons'}
                </p>

                {/* Actions */}
                <div className="flex gap-3 justify-end">
                  {onCancel && (
                    <Button variant="secondary" size="sm" onPress={onCancel}>
                      Cancel
                    </Button>
                  )}
                  <Button
                    variant="primary"
                    size="sm"
                    onPress={() =>
                      onStart({
                        lessonIds: Array.from(selectedLessonIds),
                        questionTypes: Array.from(selectedQuestionTypes),
                        maxQuestions: effectiveMax,
                      })
                    }
                    isDisabled={isCreating || selectedLessonIds.size === 0}
                  >
                    {isCreating ? 'Creating...' : `Start Revision (${effectiveMax} questions)`}
                  </Button>
                </div>

                {error && (
                  <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-error-text)' }}>
                    {error}
                  </p>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
